import React, { useState, useEffect } from "react";
import { 
  Card, 
  Row, 
  Col, 
  Statistic, 
  Alert, 
  Typography, 
  Space, 
  Tag, 
  Table, 
  Tabs,
  Progress,
  Skeleton,
  Badge,
  Tooltip
} from "antd";
import { 
  CalendarOutlined,
  RiseOutlined,
  FallOutlined,
  MinusOutlined,
  ThunderboltOutlined,
  EyeOutlined
} from "@ant-design/icons";
import { ANALYTICS_API_URL } from "../../../constants/config";
import { getAndSanitize } from "../../../shared/utils/sanitization";
import { useAnalytics } from "../../../contexts/AnalyticsContext";
import { TemporalPatterns as TemporalPatternsType } from "../../../types/analytics";

const { Title, Text } = Typography;
const { TabPane } = Tabs;

interface TemporalPatternsProps {
  loading?: boolean;
}

export const TemporalPatterns: React.FC<TemporalPatternsProps> = ({ loading: propLoading }) => {
  const [data, setData] = useState<TemporalPatternsType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const { filters } = useAnalytics();

  useEffect(() => {
    const fetchTemporalPatterns = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = getAndSanitize("token");
        const adminId = getAndSanitize("adminId");

        if (!token || !adminId) {
          setError("Token o adminId no disponible");
          return;
        }

        const queryParams = new URLSearchParams({
          adminId,
          'dateRange[start]': filters.dateRange.start,
          'dateRange[end]': filters.dateRange.end,
          ...(filters.accountName && { accountName: filters.accountName }),
        });

        const response = await fetch(`${ANALYTICS_API_URL}/api/analytics/temporal?${queryParams}`, {
          method: "GET",
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json",
            "X-Admin-ID": adminId,
          },
        });

        if (!response.ok) {
          throw new Error(`Error ${response.status}: ${response.statusText}`);
        }

        const result = await response.json();
        
        if (result.success && result.data) {
          setData(result.data);
          setLastUpdated(new Date());
        } else {
          setError(result.message || "Error al obtener patrones temporales");
        }
      } catch (err) {
        console.error("Error fetching temporal patterns:", err);
        setError(err instanceof Error ? err.message : "Error al cargar patrones temporales");
      } finally {
        setLoading(false);
      }
    };

    fetchTemporalPatterns();
  }, [filters]);

  const getTrendIcon = (trend: 'up' | 'down' | 'stable') => {
    switch (trend) {
      case 'up':
        return <RiseOutlined style={{ color: '#52c41a' }} />;
      case 'down':
        return <FallOutlined style={{ color: '#ff4d4f' }} />;
      default:
        return <MinusOutlined style={{ color: '#faad14' }} />;
    }
  };



  const getTimeTypeIcon = (peakType: 'morning' | 'afternoon' | 'evening' | 'night') => {
    switch (peakType) {
      case 'morning':
        return '🌅';
      case 'afternoon':
        return '☀️';
      case 'evening':
        return '🌆';
      default:
        return '🌙';
    }
  };

  const getCoefficientColor = (coefficient: number): string => {
    if (coefficient >= 1.3) return '#52c41a';  // Verde - muy alto
    if (coefficient >= 1.1) return '#1890ff';  // Azul - alto
    if (coefficient >= 0.9) return '#faad14';  // Amarillo - normal
    if (coefficient >= 0.7) return '#fa8c16';  // Naranja - bajo
    return '#ff4d4f';                          // Rojo - muy bajo
  };

  if (loading || propLoading) {
    return (
      <div style={{ padding: 24 }}>
        <Skeleton active />
        <Skeleton active />
        <Skeleton active />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error al cargar Patrones Temporales"
        description={error}
        type="error"
        showIcon
        style={{ margin: 24 }}
      />
    );
  }

  if (!data) {
    return (
      <Alert
        message="Sin datos disponibles"
        description="No hay suficientes datos para generar patrones temporales"
        type="info"
        showIcon
        style={{ margin: 24 }}
      />
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Title level={3}>🕒 Análisis de Patrones Temporales</Title>
          <Text type="secondary">
            Análisis de estacionalidad, ciclos y predicciones temporales
          </Text>
        </div>

        {/* Predicciones */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={8}>
            <Card>
              <Statistic
                title="Predicción Próxima Semana - Volumen"
                value={data.forecastingData.nextWeekPrediction.expectedVolume}
                prefix={<EyeOutlined />}
                suffix={
                  <Badge 
                    count={`${(data.forecastingData.nextWeekPrediction.confidence * 100).toFixed(0)}%`}
                    style={{ backgroundColor: '#52c41a' }}
                  />
                }
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <Card>
              <Statistic
                title="Predicción Próxima Semana - Monto"
                value={data.forecastingData.nextWeekPrediction.expectedAmount}
                prefix="$"
                precision={0}
                suffix={
                  <Badge 
                    count={`${(data.forecastingData.nextWeekPrediction.confidence * 100).toFixed(0)}%`}
                    style={{ backgroundColor: '#52c41a' }}
                  />
                }
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <Card>
              <Statistic
                title="Predicción Próximo Mes - Volumen"
                value={data.forecastingData.nextMonthPrediction.expectedVolume}
                prefix={<CalendarOutlined />}
                suffix={
                  <Badge 
                    count={`${(data.forecastingData.nextMonthPrediction.confidence * 100).toFixed(0)}%`}
                    style={{ backgroundColor: '#1890ff' }}
                  />
                }
              />
            </Card>
          </Col>
        </Row>

        {/* Tabs con análisis detallados */}
        <Tabs defaultActiveKey="seasonality" size="large">
          <TabPane tab="📅 Estacionalidad" key="seasonality">
            <Row gutter={[16, 16]}>
              {/* Análisis Mensual */}
              <Col xs={24} lg={12}>
                <Card title="📊 Patrones por Mes" size="small">
                  <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                    {data.seasonality.byMonth.map((month) => (
                      <div key={month.month} style={{ marginBottom: 12, padding: 8, borderBottom: '1px solid #f0f0f0' }}>
                        <Row align="middle" gutter={[8, 8]}>
                          <Col span={6}>
                            <Text strong>{month.monthName}</Text>
                          </Col>
                          <Col span={10}>
                            <Progress 
                              percent={month.coefficient * 100}
                              size="small"
                              strokeColor={getCoefficientColor(month.coefficient)}
                              format={() => `${month.coefficient.toFixed(2)}x`}
                            />
                          </Col>
                          <Col span={4}>
                            {getTrendIcon(month.trend)}
                          </Col>
                          <Col span={4}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {month.averageVolume} ops
                            </Text>
                          </Col>
                        </Row>
                      </div>
                    ))}
                  </div>
                </Card>
              </Col>

              {/* Análisis por Día de Semana */}
              <Col xs={24} lg={12}>
                <Card title="📅 Patrones por Día de Semana" size="small">
                  <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                    {data.seasonality.byDayOfWeek.map((day) => (
                      <div key={day.dayNumber} style={{ marginBottom: 12, padding: 8, borderBottom: '1px solid #f0f0f0' }}>
                        <Row align="middle" gutter={[8, 8]}>
                          <Col span={6}>
                            <Text strong>
                              {day.day.substring(0, 3)}
                              {day.isWeekend && <Tag color="orange">FDS</Tag>}
                            </Text>
                          </Col>
                          <Col span={10}>
                            <Progress 
                              percent={day.coefficient * 100}
                              size="small"
                              strokeColor={getCoefficientColor(day.coefficient)}
                              format={() => `${day.coefficient.toFixed(2)}x`}
                            />
                          </Col>
                          <Col span={4}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {day.averageVolume} ops
                            </Text>
                          </Col>
                          <Col span={4}>
                            <Tooltip title={`Horas pico: ${day.peakHours.join(', ')}`}>
                              <ThunderboltOutlined style={{ color: '#faad14' }} />
                            </Tooltip>
                          </Col>
                        </Row>
                      </div>
                    ))}
                  </div>
                </Card>
              </Col>
            </Row>

            {/* Análisis Horario */}
            <Card title="🕐 Patrones por Hora del Día" style={{ marginTop: 16 }}>
              <Row gutter={[8, 8]}>
                {data.seasonality.byHour.map((hour) => (
                  <Col xs={12} sm={8} md={6} lg={4} xl={3} key={hour.hour}>
                    <Card size="small" style={{ textAlign: 'center' }}>
                      <div style={{ marginBottom: 8 }}>
                        <Text strong>{hour.hour.toString().padStart(2, '0')}:00</Text>
                        <div>{getTimeTypeIcon(hour.peakType)}</div>
                      </div>
                      <Progress
                        percent={hour.coefficient * 100}
                        size="small"
                        strokeColor={getCoefficientColor(hour.coefficient)}
                        format={() => `${hour.coefficient.toFixed(1)}x`}
                      />
                      <div style={{ marginTop: 4 }}>
                        <Text type="secondary" style={{ fontSize: 10 }}>
                          {hour.averageVolume} ops
                        </Text>
                      </div>
                      <div>
                        <Text type="secondary" style={{ fontSize: 10 }}>
                          {(hour.efficiency * 100).toFixed(0)}% éxito
                        </Text>
                      </div>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Card>
          </TabPane>

          <TabPane tab="🔄 Ciclos de Comportamiento" key="cycles">
            <Row gutter={[16, 16]}>
              {/* Ciclos de Pago */}
              <Col xs={24} lg={12}>
                <Card title="💳 Ciclos de Pago Identificados">
                  <Table
                    dataSource={data.cycleAnalysis.paymentCycles}
                    columns={[
                      {
                        title: 'Período',
                        dataIndex: 'days',
                        key: 'days',
                        render: (days: number) => (
                          <Tag color="blue">Cada {days} días</Tag>
                        )
                      },
                      {
                        title: 'Usuarios',
                        dataIndex: 'userCount',
                        key: 'userCount',
                        render: (count: number) => <Text strong>{count}</Text>
                      },
                      {
                        title: 'Confiabilidad',
                        dataIndex: 'reliability',
                        key: 'reliability',
                        render: (reliability: number) => (
                          <Progress 
                            percent={reliability * 100}
                            size="small"
                            strokeColor="#52c41a"
                            format={() => `${(reliability * 100).toFixed(0)}%`}
                          />
                        )
                      }
                    ]}
                    pagination={false}
                    size="small"
                  />
                </Card>
              </Col>

              {/* Patrones de Comportamiento */}
              <Col xs={24} lg={12}>
                <Card title="🎯 Patrones de Comportamiento">
                  <Table
                    dataSource={data.cycleAnalysis.behaviorCycles}
                    columns={[
                      {
                        title: 'Patrón',
                        dataIndex: 'pattern',
                        key: 'pattern',
                        render: (pattern: string) => {
                          const patternNames: Record<string, { label: string; color: string }> = {
                            'weekly_regular': { label: '📅 Semanal Regular', color: 'green' },
                            'monthly_spender': { label: '📆 Mensual', color: 'blue' },
                            'sporadic': { label: '🎲 Esporádico', color: 'orange' }
                          };
                          const info = patternNames[pattern] || { label: pattern, color: 'default' };
                          return <Tag color={info.color}>{info.label}</Tag>;
                        }
                      },
                      {
                        title: 'Usuarios',
                        dataIndex: 'userCount',
                        key: 'userCount',
                        render: (count: number) => <Text strong>{count}</Text>
                      },
                      {
                        title: 'Predictibilidad',
                        dataIndex: 'predictability',
                        key: 'predictability',
                        render: (pred: number) => (
                          <Progress 
                            percent={pred * 100}
                            size="small"
                            strokeColor="#1890ff"
                            format={() => `${(pred * 100).toFixed(0)}%`}
                          />
                        )
                      }
                    ]}
                    pagination={false}
                    size="small"
                  />
                </Card>
              </Col>
            </Row>
          </TabPane>

          <TabPane tab="📈 Tendencias y Predicciones" key="forecasting">
            <Card title="🔮 Análisis de Tendencias">
              <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                  <Card title="📊 Predicciones a Corto Plazo" size="small">
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <div>
                        <Text strong>Próxima Semana:</Text>
                        <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
                          <Col span={12}>
                            <Statistic
                              title="Volumen Esperado"
                              value={data.forecastingData.nextWeekPrediction.expectedVolume}
                              suffix="ops"
                              valueStyle={{ fontSize: '16px' }}
                            />
                          </Col>
                          <Col span={12}>
                            <Statistic
                              title="Monto Esperado"
                              value={data.forecastingData.nextWeekPrediction.expectedAmount}
                              prefix="$"
                              precision={0}
                              valueStyle={{ fontSize: '16px' }}
                            />
                          </Col>
                        </Row>
                        <Progress 
                          percent={data.forecastingData.nextWeekPrediction.confidence * 100}
                          strokeColor="#52c41a"
                          format={() => `Confianza: ${(data.forecastingData.nextWeekPrediction.confidence * 100).toFixed(0)}%`}
                        />
                      </div>

                      <div style={{ marginTop: 16 }}>
                        <Text strong>Próximo Mes:</Text>
                        <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
                          <Col span={12}>
                            <Statistic
                              title="Volumen Esperado"
                              value={data.forecastingData.nextMonthPrediction.expectedVolume}
                              suffix="ops"
                              valueStyle={{ fontSize: '16px' }}
                            />
                          </Col>
                          <Col span={12}>
                            <Statistic
                              title="Monto Esperado"
                              value={data.forecastingData.nextMonthPrediction.expectedAmount}
                              prefix="$"
                              precision={0}
                              valueStyle={{ fontSize: '16px' }}
                            />
                          </Col>
                        </Row>
                        <Progress 
                          percent={data.forecastingData.nextMonthPrediction.confidence * 100}
                          strokeColor="#1890ff"
                          format={() => `Confianza: ${(data.forecastingData.nextMonthPrediction.confidence * 100).toFixed(0)}%`}
                        />
                      </div>
                    </Space>
                  </Card>
                </Col>

                <Col xs={24} md={12}>
                  <Card title="📊 Análisis de Tendencias" size="small">
                    {data.forecastingData.trends.map((trend) => (
                      <div key={trend.period} style={{ marginBottom: 16 }}>
                        <Row align="middle" gutter={[8, 8]}>
                          <Col span={8}>
                            <Text strong>{trend.period}</Text>
                          </Col>
                          <Col span={4}>
                            {getTrendIcon(trend.direction)}
                          </Col>
                          <Col span={6}>
                            <Text>{trend.direction === 'up' ? 'Creciente' : 
                                   trend.direction === 'down' ? 'Decreciente' : 'Estable'}</Text>
                          </Col>
                          <Col span={6}>
                            <Progress
                              percent={trend.significance * 100}
                              size="small"
                              strokeColor="#722ed1"
                              format={() => `${(trend.significance * 100).toFixed(0)}%`}
                            />
                          </Col>
                        </Row>
                      </div>
                    ))}
                  </Card>
                </Col>
              </Row>
            </Card>
          </TabPane>
        </Tabs>

        {/* Footer con información */}
        <Card size="small">
          <Text type="secondary">
            📅 Última actualización: {lastUpdated.toLocaleString('es-ES')} | 
            💡 Los patrones se calculan en base a datos históricos y tendencias estadísticas
          </Text>
        </Card>
      </Space>
    </div>
  );
};

export default TemporalPatterns; 